// Reports `turbo:frame-missing` ("Content missing") and
// `turbo:fetch-request-error` to two sinks:
//
//   1. POST /turbo_diagnostics → AuditLogger + Rails.logger + Sentry
//   2. ahoy.track → ahoy_events tied to the visit / user
//
// Listeners do not preventDefault — Turbo's native handling still runs.

import ahoy from 'ahoy.js';

const REPORT_BASE = '/turbo_diagnostics';
const BODY_PREVIEW_BYTES = 2048;

const AHOY_EVENT_NAMES = {
  frame_missing: 'turbo:frame-missing',
  fetch_request_error: 'turbo:fetch-request-error',
};

// Stash a clone of in-flight frame responses so that if Turbo can't find
// the matching frame we can still include a snippet of what came back —
// by the time `turbo:frame-missing` fires the original body has already
// been consumed.
const frameResponseBodies = new WeakMap();

document.addEventListener('turbo:before-fetch-response', (event) => {
  if (event.target?.tagName !== 'TURBO-FRAME') return;

  const response = event.detail?.fetchResponse?.response;
  if (!response) return;

  const clone = response.clone();
  const bodyPromise = clone.text().then(
    (text) => text.slice(0, BODY_PREVIEW_BYTES),
    () => null,
  );
  frameResponseBodies.set(response, bodyPromise);
});

document.addEventListener('turbo:frame-missing', async (event) => {
  const frame = event.target;
  const response = event.detail?.response;
  const bodyPreview = response ? await frameResponseBodies.get(response) : null;

  reportDiagnostic('frame_missing', {
    ...frameAttributes(frame),
    ...pageContext(),
    response_url: response?.url,
    response_status: response?.status,
    response_redirected: response?.redirected,
    response_content_type: response?.headers?.get('content-type'),
    response_body_preview: bodyPreview ?? null,
  });
});

document.addEventListener('turbo:fetch-request-error', (event) => {
  const target = event.target;
  const request = event.detail?.request;
  const error = event.detail?.error;

  reportDiagnostic('fetch_request_error', {
    ...(target?.tagName === 'TURBO-FRAME' ? frameAttributes(target) : {}),
    ...pageContext(),
    request_url: request?.url,
    request_method: request?.method,
    error_message: error?.message ?? (error ? String(error) : null),
  });
});

function frameAttributes(frame) {
  if (!frame) return {};
  return {
    frame_id: frame.id || null,
    frame_src: frame.getAttribute('src'),
    frame_loading: frame.loading,
    frame_target: frame.getAttribute('target'),
    frame_complete: frame.hasAttribute('complete'),
  };
}

function pageContext() {
  return {
    page_url: window.location.href,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
}

// Best-effort fan-out — never let telemetry break the page.
function reportDiagnostic(kind, payload) {
  fetch(`${REPORT_BASE}/${kind}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  }).catch(() => {});

  try {
    ahoy.track(AHOY_EVENT_NAMES[kind], payload);
  } catch (_e) { /* swallow */ }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}
